import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  Plus,
  Trash2,
  ChevronDown,
  GripVertical,
  AlertCircle,
  MoreHorizontal,
  Copy
} from 'lucide-react';

import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { useOrderActions } from '../../../../contexts/OrderActionsContext';
import OrdenoLoading from '../../../../components/OrdenoLoading';
import {
  createProduct,
  updateProduct,
  patchProductAttachments,
  fetchProductById,
  fetchResources,
  fetchServices,
  fetchRawMaterials,
  fetchProducts,
  fetchCompaniesByStage,
  transformProductDataForBackend,
  transformScriptsDataForBackend,
  createScript,
  updateScript as updateScriptAPI,
  deleteScriptItem,
  deleteScript,
  createScriptItem,
  updateScriptItemData,
  deleteProduct,
  isTemporaryId,
} from './api';
import { Product } from './types';
import { productFinalSchema, ProductFinalSchemaType } from './product-final.schema';
import { initialProductFinal } from './constants';
import InfiniteSelect from '../../../../components/InfiniteSelect';
import { NcmCatalogoOption, codigoNcmDe, ncmCatalogoEndpoint, ncmDaOpcao, ncmSelecionado } from '../../../../utils/ncmCatalogo';
import ApiService from '../../../../services/ApiService';
import { SortableItem } from './components/SortableItem';

import {
  Container,
  Title,
  Card,
  SubTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  Button,
  AddNewItemButton,
  AddProductButton,
  Separator,
  CollapsibleHeader,
  CircularCollapseIcon,
  ExpandedItemContainer,
  PageGrid,
  FormColumn,
  AsideColumn,
} from './style';
import AddItemModal from './AddItemModal';
import ConfirmationModal from '../../../../components/ModalConfirmation';
import Tooltip from '../../../../components/Tooltip';
import { ExistingAttachment } from '../../../../components/AttachmentManager';
import ProductAttachmentsAside from '../../ProductComposition/RegisterComposition/AttachmentsAside';
import { downloadRemoteFile } from '../../../../utils/downloadRemoteFile';
import { openSignedAttachment, getSignedAttachmentUrl } from '../../../../utils/openSignedAttachment';
import {
  attachmentsFromProductResponse,
  getProductAttachmentActionUrl,
  mergeAttachmentUrls,
  buildAttachmentFieldsForSave,
  setProductAttachmentCover,
  removeProductAttachmentCover,
} from './productAttachments';

const getItemRealId = (item: any): number | null => {
  if (item?.realId) return item.realId;
  return null;
};

const getSortableId = (item: any): string => {
  const realId = getItemRealId(item);
  if (realId) return `item_${realId}`;
  return String(item.id);
};

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { productId } = useParams<{ productId: string }>();
  const { setFeedback } = useContext(FeedbackContext);
  const { setSaveOrderHandler } = useOrderActions();
  const queryClient = useQueryClient();

  const [productSelectedFiles, setProductSelectedFiles] = useState<File[]>([]);
  const [productExistingAttachments, setProductExistingAttachments] = useState<ExistingAttachment[]>([]);
  const [productAttachmentUrls, setProductAttachmentUrls] = useState<string[]>([]);
  const [coverAttachmentId, setCoverAttachmentId] = useState<number | null>(null);

  const ATTACHMENTS_ASIDE_STORAGE_KEY = 'steelProductAttachmentsAsideOpen';
  const [isAttachmentsAsideOpen, setIsAttachmentsAsideOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(ATTACHMENTS_ASIDE_STORAGE_KEY);
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch {
      return true;
    }
    return true;
  });

  useEffect(() => {
    try {
      localStorage.setItem(ATTACHMENTS_ASIDE_STORAGE_KEY, isAttachmentsAsideOpen ? '1' : '0');
    } catch {
      return;
    }
  }, [isAttachmentsAsideOpen]);

  const attachmentsDirtyRef = useRef(false);
  const attachmentsRef = useRef({
    selectedFiles: [] as File[],
    urls: [] as string[],
    existing: [] as ExistingAttachment[],
    coverId: null as number | null,
  });
  attachmentsRef.current = {
    selectedFiles: productSelectedFiles,
    urls: productAttachmentUrls,
    existing: productExistingAttachments,
    coverId: coverAttachmentId,
  };

  const isEditMode = Boolean(productId);
  const isDuplicateMode = Boolean(location.state?.duplicateData);
  const [isFormReady, setIsFormReady] = useState(!isEditMode && !isDuplicateMode);

  const { data: existingProduct, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => fetchProductById(productId!),
    enabled: isEditMode,
    staleTime: 0,
  });

  const { mutate: submitProduct, isPending: isSubmitting } = useMutation({
    mutationFn: async (data: any) => {
      const { attachmentMeta, ...formRest } = data;
      const attachmentFields = attachmentMeta
        ? await buildAttachmentFieldsForSave(
            attachmentMeta,
            isEditMode ? productId : undefined,
          )
        : {};

      const { attachments: attachmentUrls, coverGeneralFileId } = attachmentFields;
      const productData = transformProductDataForBackend(formRest);
      const shouldPersistCover = Boolean(attachmentMeta?.shouldPersistCover);

      const persistAttachmentsOnly = async (targetProductId: string) => {
        if (attachmentUrls !== undefined) {
          await patchProductAttachments(targetProductId, attachmentUrls);
        }

        if (shouldPersistCover) {
          if (
            typeof coverGeneralFileId === 'number' &&
            coverGeneralFileId > 0 &&
            coverGeneralFileId < 100000
          ) {
            const currentCoverAtt = attachmentsRef.current.existing.find(
              (a: ExistingAttachment) => a.id === coverGeneralFileId || a.isCover,
            );
            const scaleToSave =
              typeof currentCoverAtt?.coverScale === 'number'
                ? currentCoverAtt.coverScale
                : typeof attachmentMeta?.coverImageScale === 'number'
                ? attachmentMeta.coverImageScale
                : undefined;
            const posXToSave =
              typeof currentCoverAtt?.coverPosX === 'number'
                ? currentCoverAtt.coverPosX
                : typeof attachmentMeta?.coverImagePosX === 'number'
                ? attachmentMeta.coverImagePosX
                : undefined;
            const posYToSave =
              typeof currentCoverAtt?.coverPosY === 'number'
                ? currentCoverAtt.coverPosY
                : typeof attachmentMeta?.coverImagePosY === 'number'
                ? attachmentMeta.coverImagePosY
                : undefined;

            await setProductAttachmentCover(
              targetProductId,
              coverGeneralFileId,
              scaleToSave,
              posXToSave,
              posYToSave,
            );
          } else if (coverGeneralFileId === null) {
            await removeProductAttachmentCover(targetProductId);
          }
        }
      };

      if (isEditMode) {
        const updatedProduct = await updateProduct(productId!, productData);
        await persistAttachmentsOnly(productId!);

        if (data.scripts && Array.isArray(data.scripts)) {
          const originalScriptIds = existingProduct?.scripts?.map((s: any) => s.id) || [];
          const currentScriptIds = data.scripts
            .map((s: any) => s.id)
            .filter((id: any) => !isTemporaryId(id));

          const removedScriptIds = originalScriptIds.filter((id: any) => !currentScriptIds.includes(id));
          for (const scriptId of removedScriptIds) {
            await deleteScript(scriptId);
          }

          for (const script of data.scripts) {
            if (isTemporaryId(script.id)) {
              const [newScriptData] = transformScriptsDataForBackend([script], parseInt(productId!));
              await createScript(newScriptData);
              continue;
            }

            if (script.items && Array.isArray(script.items)) {
              script.items = script.items.map((item: any, index: number) => ({ ...item, order: index + 1 }));
              const originalScript = existingProduct?.scripts?.find((s: any) => s.id === script.id);
              if (originalScript) {
                const originalItems = originalScript.items ?? [];

                if (script.name !== originalScript.name) {
                  await updateScriptAPI(script.id.toString(), { name: script.name });
                }

                if (script.additionalDeadline !== originalScript.additionalDeadline) {
                  const currentValue = script.additionalDeadline;
                  const originalValue = originalScript.additionalDeadline;
                  if (currentValue !== originalValue) {
                    try {
                      await updateScriptAPI(script.id, { additionalDeadline: currentValue });
                    } catch (error) {
                      console.error('Erro ao atualizar additionalDeadline:', error);
                    }
                  }
                }

                for (const item of script.items) {
                  const itemRealId = getItemRealId(item);
                  const isNewItem =
                    isTemporaryId(item.id) ||
                    (!itemRealId && !originalItems.some((orig: any) =>
                      String(orig.id) === String(item.id)
                    ));

                  if (isNewItem) {
                    await createScriptItem(script.id, item);
                  }
                }

                for (const item of script.items) {
                  if (isTemporaryId(item.id)) continue;

                  const itemRealId = getItemRealId(item);
                  const originalItem = originalItems.find((orig: any) => {
                    if (itemRealId && orig.realId) return orig.realId === itemRealId;
                    return String(orig.id) === String(item.id);
                  });

                  if (originalItem) {
                    const hasItemChanged =
                      Number(item.order ?? 0) !== Number(originalItem.order ?? 0) ||
                      item.weight !== originalItem.weight ||
                      item.idealMeasurements !== originalItem.idealMeasurements ||
                      item.executionTime !== originalItem.executionTime ||
                      item.setupTime !== originalItem.setupTime ||
                      (item.company?.id || item.companyId) !== (originalItem.company?.id || originalItem.companyId) ||
                      item.requiredQualification !== originalItem.requiredQualification ||
                      item.deliveryTime !== originalItem.deliveryTime ||
                      item.quantity !== originalItem.quantity ||
                      item.unitOfMeasurementId !== originalItem.unitOfMeasurementId ||
                      item.description !== originalItem.description ||
                      item.observation !== originalItem.observation ||
                      JSON.stringify(item.rawMaterial) !== JSON.stringify(originalItem.rawMaterial) ||
                      JSON.stringify(item.service) !== JSON.stringify(originalItem.service) ||
                      JSON.stringify(item.resource) !== JSON.stringify(originalItem.resource) ||
                      JSON.stringify(item.productFinal) !== JSON.stringify(originalItem.productFinal) ||
                      JSON.stringify(item.productScript) !== JSON.stringify(originalItem.productScript);

                    if (hasItemChanged) {
                      await updateScriptItemData(script.id, item);
                    }
                  }
                }

                for (const originalItem of originalItems) {
                  const origRealId = getItemRealId(originalItem);
                  const itemStillExists = script.items.some((item: any) => {
                    if (origRealId && item.realId) return item.realId === origRealId;
                    return String(item.id) === String(originalItem.id);
                  });

                  if (!itemStillExists) {
                    await deleteScriptItem(script.id, originalItem);
                  }
                }
              }
            }
          }
        }
        return updatedProduct;
      } else {
        const newProduct = await createProduct(productData);

        if (!newProduct || !newProduct.id) {
          throw new Error('Failed to create product: No ID returned.');
        }

        await persistAttachmentsOnly(String(newProduct.id));

        const scriptsData = transformScriptsDataForBackend(data.scripts, newProduct.id);
        const createdScripts: any[] = [];

        try {
          for (const script of scriptsData) {
            const newScript = await createScript(script);
            createdScripts.push(newScript);
          }
        } catch (error) {
          if (createdScripts.length > 0) {
            console.error('Erro ao criar alguns scripts, mas produto será mantido pois pelo menos um script foi criado:', error);

            if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.includes('script duplicado')) {
              throw new Error(`Produto criado com ${createdScripts.length} script(s), mas alguns falharam: ${error.message}. Verifique se os scripts não são duplicados.`);
            } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.includes('já existe')) {
              throw new Error(`Produto criado com ${createdScripts.length} script(s), mas alguns falharam: ${error.message}. Verifique se os itens não são duplicados.`);
            } else {
              const errorMessage = error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
                ? error.message
                : 'Erro desconhecido';
              throw new Error(`Produto criado com ${createdScripts.length} script(s), mas alguns falharam: ${errorMessage}`);
            }
          } else {
            console.error('Erro ao criar scripts, excluindo produto:', error);
            try {
              await deleteProduct(newProduct.id.toString());
            } catch (deleteError) {
              console.error('Erro ao excluir produto após falha:', deleteError);
            }

            if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.includes('script duplicado')) {
              throw new Error(`Não foi possível criar o produto: ${error.message}. Verifique se os scripts não são duplicados.`);
            } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.includes('já existe')) {
              throw new Error(`Não foi possível criar o produto: ${error.message}. Verifique se os itens não são duplicados.`);
            } else {
              const errorMessage = error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
                ? error.message
                : 'Erro desconhecido';
              throw new Error(`Não foi possível criar o produto: ${errorMessage}`);
            }
          }
        }
        return newProduct;
      }
    },
    onSuccess: (data: any) => {
      const action = isEditMode ? 'atualizado' : 'criado';

      attachmentsDirtyRef.current = false;
      setProductSelectedFiles([]);

      if (data?.attachmentFiles?.length) {
        const attachmentState = attachmentsFromProductResponse(data);
        setProductExistingAttachments(attachmentState.existing);
        setProductAttachmentUrls(attachmentState.urls);
        setCoverAttachmentId(attachmentState.coverId);
      }

      if (isEditMode && productId) {
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['paginatedProducts'] });
      }

      setFeedback({ message: `Produto ${data.id || ''} ${action} com sucesso!`, isOpen: true, statusOk: true });
      navigate(returnTo || '/panel/product-final');
    },
    onError: (error: any) => {
      const action = isEditMode ? 'atualizar' : 'criar';

      let errorMessage = 'Erro desconhecido';
      let isConflictError = false;

      if (error?.response?.status === 409) {
        isConflictError = true;
        errorMessage = error?.response?.data?.message || 'Conflito: Este item já existe ou criaria um script duplicado.';
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
        if (errorMessage.includes('script duplicado') || errorMessage.includes('já existe') || errorMessage.includes('duplicado')) {
          isConflictError = true;
        }
      } else if (error?.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique as informações inseridas.';
      } else if (error?.response?.status === 500) {
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'Erro interno do servidor. Tente novamente.';
        }
      } else if (error?.message) {
        errorMessage = error.message;
        if (errorMessage.includes('script duplicado') || errorMessage.includes('já existe') || errorMessage.includes('duplicado')) {
          isConflictError = true;
        }
      }

      setFeedback({
        message: `Erro ao ${action} produto: ${errorMessage}`,
        isOpen: true,
        statusOk: false,
        timeout: isConflictError ? 10000 : 5000
      });
    },
  });

  const [
    resourcesQuery,
    servicesQuery,
    rawMaterialsQuery,
    productsQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: ['resources'],
        queryFn: () => fetchResources(100, 0),
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false
      },
      {
        queryKey: ['services'],
        queryFn: () => fetchServices(100, 0),
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false
      },
      {
        queryKey: ['rawMaterials'],
        queryFn: () => fetchRawMaterials(100, 0),
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false
      },
      {
        queryKey: ['products'],
        queryFn: () => fetchProducts(100, 0),
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false
      },
    ],
  });

  const fetchProductsFiltered = useCallback(async (limit: number = 10, offset: number = 0, search?: string) => {
    const allProducts = await fetchProducts(100, 0, search);

    if (isEditMode && productId) {
      return allProducts.filter(product => product.id !== parseInt(productId));
    }

    return allProducts;
  }, [isEditMode, productId]);

  const [expandedScripts, setExpandedScripts] = useState<Set<number>>(new Set([0]));
  const [expandedItems, setExpandedItems] = useState<Set<string | number>>(new Set());
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [currentScriptIndex, setCurrentScriptIndex] = useState<number | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteScriptModal, setShowDeleteScriptModal] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState<number | null>(null);
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ scriptIndex: number; itemId: string | number } | null>(null);
  const [scriptMenuOpen, setScriptMenuOpen] = useState<number | null>(null);
  const [validationErrorModal, setValidationErrorModal] = useState<{ isOpen: boolean; message: React.ReactNode; errorFields: string[] }>({
    isOpen: false,
    message: '',
    errorFields: [],
  });
  const [errorFieldsForNavigation, setErrorFieldsForNavigation] = useState<string[]>([]);
  const expandedScriptsRef = useRef<Set<number>>(new Set([0]));

  const methods = useForm<ProductFinalSchemaType>({
    resolver: zodResolver(productFinalSchema),
    defaultValues: initialProductFinal,
    mode: 'onSubmit',
  });

  const { control, handleSubmit, formState: { errors }, watch, setValue, reset, getValues } = methods;

  // NCM opcional no produto (habilita o engenheirado na NF-e, PRD 5), escolhido no catálogo oficial.
  const [ncm, setNcm] = useState<NcmCatalogoOption | null>(null);

  const { data: formats = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ['format-list'],
    queryFn: async () => {
      const res = await ApiService.stockLookup.getFormats();
      return Array.isArray(res?.data) ? res.data : [];
    },
  });

  const { data: units = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ['unit-list'],
    queryFn: async () => {
      const res = await ApiService.services.getUnits();
      return Array.isArray(res?.data) ? res.data : [];
    },
  });

  const { fields: scripts, append: appendScript, remove: removeScriptField, update: updateScriptField } = useFieldArray({
    control,
    name: 'scripts',
  });

  const watchedScripts = watch('scripts') || [];

  const updateScript = (scriptIndex: number, updatedScript: any) => {
    updateScriptField(scriptIndex, updatedScript);
  };

  const updateItemWeight = useCallback((scriptIndex: number, itemIndex: number, value: any) => {
    setValue(`scripts.${scriptIndex}.items.${itemIndex}.weight` as any, value);
  }, [setValue]);

  const updateItemIdealMeasurements = useCallback((scriptIndex: number, itemIndex: number, value: string) => {
    setValue(`scripts.${scriptIndex}.items.${itemIndex}.idealMeasurements` as any, value);
  }, [setValue]);

  const updateItemProductScript = useCallback(async (scriptIndex: number, itemIndex: number, value: any) => {
    setValue(`scripts.${scriptIndex}.items.${itemIndex}.productScript` as any, value);

    if (isEditMode) {
      const currentFormData = getValues();
      const item = currentFormData.scripts?.[scriptIndex]?.items?.[itemIndex];

      if (item && !isTemporaryId(item.id)) {
        try {
          const scriptId = currentFormData.scripts[scriptIndex].id;
          const itemWithScript = { ...item, productScript: value };
          await createScriptItem(scriptId, itemWithScript);

          queryClient.invalidateQueries({ queryKey: ['product', productId] });
        } catch (error) {
          console.error('Erro ao atualizar script do produto:', error);
        }
      }
    }
  }, [setValue, isEditMode, getValues, productId, queryClient]);

  const updateItemReadyToExecute = useCallback((scriptIndex: number, itemIndex: number, value: boolean) => {
    setValue(`scripts.${scriptIndex}.items.${itemIndex}.readyToExecute` as any, value);
  }, [setValue]);

  const updateItemUnitOfMeasurement = useCallback(async (scriptIndex: number, itemIndex: number, unitId: number) => {
    setValue(`scripts.${scriptIndex}.items.${itemIndex}.unitOfMeasurementId` as any, unitId);

    if (isEditMode) {
      const currentFormData = getValues();
      const item = currentFormData.scripts?.[scriptIndex]?.items?.[itemIndex];

      if (item && !isTemporaryId(item.id)) {
        try {
          const scriptId = currentFormData.scripts[scriptIndex].id;
          const itemWithUnit = { ...item, unitOfMeasurementId: unitId };
          await createScriptItem(scriptId, itemWithUnit);

          queryClient.invalidateQueries({ queryKey: ['product', productId] });
        } catch (error) {
          console.error('Erro ao atualizar unidade do item:', error);
        }
      }
    }
  }, [setValue, isEditMode, getValues, productId, queryClient]);

  const validateProductFinalSelection = useCallback((selectedProduct: any, scriptIndex: number, itemIndex: number) => {
    if (isEditMode && productId && selectedProduct && selectedProduct.id === parseInt(productId)) {
      setValue(`scripts.${scriptIndex}.items.${itemIndex}.productFinal`, null);
      setFeedback({
        message: 'Não é possível selecionar o próprio produto como item do script',
        isOpen: true,
        statusOk: false
      });

      setTimeout(() => {
        setFeedback(prev => ({ ...prev, isOpen: false }));
      }, 5000);

      return false;
    }
    return true;
  }, [isEditMode, productId, setValue, setFeedback]);

  useEffect(() => {
    if (existingProduct && isEditMode && resourcesQuery.data && servicesQuery.data) {
      const enrichAndSetFormData = async (product: Product) => {

        const enrichedScripts = await Promise.all(
          (product.scripts || []).map(async (script) => {
            if (!script.items) return script;

            const enrichedItems = await Promise.all(
              script.items.map(async (item: any) => {
                const enrichedItem = { ...item };

                if (item.unitOfMeasurementId) {
                  enrichedItem.unitOfMeasurementId = item.unitOfMeasurementId;
                }

                if (enrichedItem.type === 'service') {
                  if (enrichedItem.serviceName) {
                    enrichedItem.service = { id: enrichedItem.service?.id || enrichedItem.serviceId, name: enrichedItem.serviceName };

                    const fullServiceData = servicesQuery.data?.find(s => s.id === enrichedItem.service.id);
                    if (fullServiceData) {
                      enrichedItem.service = fullServiceData;
                    }
                  }

                  if (enrichedItem.unitOfMeasurementId && enrichedItem.service?.prices) {
                    const matchingPrice = enrichedItem.service.prices.find((price: any) =>
                      price.unitOfMeasurement?.id === enrichedItem.unitOfMeasurementId
                    );
                    if (matchingPrice?.unitOfMeasurement) {
                      enrichedItem.unitOfMeasurement = matchingPrice.unitOfMeasurement;
                    }
                  }

                  if (enrichedItem.companyId && enrichedItem.companyName) {
                    enrichedItem.company = { id: enrichedItem.companyId, name: enrichedItem.companyName };
                  }
                }

                if (enrichedItem.type === 'resource') {
                  if (enrichedItem.resourceName) {
                    enrichedItem.resource = { id: enrichedItem.resource?.id || enrichedItem.resourceId, name: enrichedItem.resourceName };

                    const fullResourceData = resourcesQuery.data?.find(r => r.id === enrichedItem.resource.id);
                    if (fullResourceData) {
                      enrichedItem.resource = fullResourceData;
                    }
                  }

                  if (enrichedItem.unitOfMeasurementId && enrichedItem.resource?.prices) {
                    const matchingPrice = enrichedItem.resource.prices.find((price: any) =>
                      price.unitOfMeasurement?.id === enrichedItem.unitOfMeasurementId
                    );
                    if (matchingPrice?.unitOfMeasurement) {
                      enrichedItem.unitOfMeasurement = matchingPrice.unitOfMeasurement;
                    }
                  }
                }

                if (enrichedItem.type === 'productFinal') {
                  if (enrichedItem.productScript && enrichedItem.productScriptName) {
                    const scriptId = parseInt(String(enrichedItem.productScript), 10);
                    if (!isNaN(scriptId)) {
                      enrichedItem.productScript = {
                        id: scriptId,
                        name: enrichedItem.productScriptName,
                      };
                    } else {
                      enrichedItem.productScript = null;
                    }
                  } else {
                    enrichedItem.productScript = null;
                  }
                }

                return enrichedItem;
              })
            );
            return { ...script, items: enrichedItems };
          })
        );

        const enrichedProduct = { ...product, scripts: enrichedScripts };
        reset(enrichedProduct);
        setNcm(ncmSelecionado(product.ncmId, codigoNcmDe(product)));
        const attachmentState = attachmentsFromProductResponse(product);
        setProductExistingAttachments(attachmentState.existing);
        setProductAttachmentUrls(attachmentState.urls);
        setCoverAttachmentId(attachmentState.coverId);
        setProductSelectedFiles([]);
        attachmentsDirtyRef.current = false;
        void hydrateSignedPreviewUrls(attachmentState.existing);
        setIsFormReady(true);

        if (enrichedScripts.length > 0) {
          const allScriptIndexes = enrichedScripts.map((_, index) => index);
          setExpandedScripts(new Set(allScriptIndexes));
        }
      };

      enrichAndSetFormData(existingProduct);
    }
  }, [existingProduct, isEditMode, reset, resourcesQuery.data, servicesQuery.data]);

  useEffect(() => {
    if (isDuplicateMode && location.state?.duplicateData) {
      const duplicateData = location.state.duplicateData;
      reset(duplicateData);
      setIsFormReady(true);

      if (duplicateData.scripts && duplicateData.scripts.length > 0) {
        const allScriptIndexes = duplicateData.scripts.map((_: any, index: number) => index);
        setExpandedScripts(new Set(allScriptIndexes));
      }

      window.history.replaceState({}, document.title);
    }
  }, [isDuplicateMode, location.state, reset]);

  const handleSave = useCallback(async () => {
      try {
        const isValid = await methods.trigger();

        if (!isValid) {
          const { errors } = methods.formState;

          const errorFields: string[] = [];
          const fieldPathsToFocus: string[] = [];
          const scriptsToExpand = new Set<number>();
          const itemsToExpand = new Set<string | number>();

          if (errors.name) {
            errorFields.push('Nome do produto');
            fieldPathsToFocus.push('name');
          }
          if (errors.norm) {
            errorFields.push('Norma');
            fieldPathsToFocus.push('norm');
          }
          if (errors.quantity) {
            errorFields.push('Quantidade');
            fieldPathsToFocus.push('quantity');
          }

          if (Array.isArray(errors.scripts)) {
            errors.scripts.forEach((scriptError: any, scriptIndex: number) => {
              if (scriptError?.name) {
                errorFields.push(`Nome do script #${scriptIndex + 1}`);
                fieldPathsToFocus.push(`scripts.${scriptIndex}.name`);
                scriptsToExpand.add(scriptIndex);
              }
              if (scriptError?.items?.message === 'O script deve ter pelo menos um item.') {
                errorFields.push(`Script "${methods.getValues(`scripts.${scriptIndex}.name`) || `#${scriptIndex + 1}`}" deve ter pelo menos um item`);
                scriptsToExpand.add(scriptIndex);
              } else if (scriptError?.items && !Array.isArray(scriptError.items) && scriptError.items.message === 'O script deve ter pelo menos um item.') {
                errorFields.push(`Script "${methods.getValues(`scripts.${scriptIndex}.name`) || `#${scriptIndex + 1}`}" deve ter pelo menos um item`);
                scriptsToExpand.add(scriptIndex);
              } else if (scriptError?.items && typeof scriptError.items === 'object' && scriptError.items.message === 'O script deve ter pelo menos um item.') {
                errorFields.push(`Script "${methods.getValues(`scripts.${scriptIndex}.name`) || `#${scriptIndex + 1}`}" deve ter pelo menos um item`);
                scriptsToExpand.add(scriptIndex);
              } else if (scriptError?.items && scriptError.items.root) {
                errorFields.push(`Script "${methods.getValues(`scripts.${scriptIndex}.name`) || `#${scriptIndex + 1}`}" deve ter pelo menos um item`);
                scriptsToExpand.add(scriptIndex);
              } else if (Array.isArray(scriptError?.items)) {
                scriptError.items.forEach((itemError: any, itemIndex: number) => {
                  if (itemError) {
                    const scriptName = methods.getValues(`scripts.${scriptIndex}.name`) || `Script #${scriptIndex + 1}`;
                    const item = methods.getValues(`scripts.${scriptIndex}.items.${itemIndex}`);

                    scriptsToExpand.add(scriptIndex);
                    itemsToExpand.add(item.id);

                    if (itemError.rawMaterial) {
                      errorFields.push(`Matéria-prima no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.rawMaterial`);
                    }
                    if (itemError.service) {
                      errorFields.push(`Serviço no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.service`);
                    }
                    if (itemError.resource) {
                      errorFields.push(`Insumo no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.resource`);
                    }
                    if (itemError.productFinal) {
                      errorFields.push(`Produto Manufaturado no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.productFinal`);
                    }
                    if (itemError.productScript) {
                      errorFields.push(`Script do produto no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.productScript`);
                    }
                    if (itemError.quantity) {
                      errorFields.push(`Quantidade no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.quantity`);
                    }
                    if (itemError.company) {
                      errorFields.push(`Fornecedor no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.company`);
                    }
                    if (itemError.deliveryTime) {
                      errorFields.push(`Prazo de entrega no item #${itemIndex + 1} (${scriptName})`);
                      fieldPathsToFocus.push(`scripts.${scriptIndex}.items.${itemIndex}.deliveryTime`);
                    }
                  }
                });
              }
            });
          }

          let errorMessage;
          if (errorFields.length > 0) {
            const uniqueErrors = [...new Set(errorFields)];
            errorMessage = <>Por favor, corrija os seguintes <span style={{ fontWeight: 600 }}>campos obrigatórios</span>: {uniqueErrors.join(', ')}</>;
          }

          setValidationErrorModal({
            isOpen: true,
            message: errorMessage,
            errorFields: fieldPathsToFocus,
          });

          setErrorFieldsForNavigation(fieldPathsToFocus);

          setExpandedScripts(prev => new Set([...prev, ...scriptsToExpand]));
          setExpandedItems(prev => new Set([...prev, ...itemsToExpand]));

          return;
        }

        setErrorFieldsForNavigation([]);
        const formData = methods.getValues();

        if (formData.scripts && formData.scripts.length > 0) {
          for (let i = 0; i < formData.scripts.length; i++) {
            const script = formData.scripts[i];
            if (!script.items || script.items.length === 0) {
              setFeedback({
                message: `Script "${script.name || `#${i + 1}`}" deve ter pelo menos um item.`,
                isOpen: true,
                statusOk: false,
              });
              return;
            }
          }
        }

        const att = attachmentsRef.current;
        const baseUrls = mergeAttachmentUrls(att.urls, att.existing);
        const shouldPersistAttachments =
          attachmentsDirtyRef.current ||
          att.selectedFiles.length > 0 ||
          baseUrls.length > 0;

        const coverFromList = att.existing.find((a) => a.isCover);
        let coverGeneralFileId: number | null | undefined;
        if (coverFromList && coverFromList.id > 0 && coverFromList.id < 100000) {
          coverGeneralFileId = coverFromList.id;
        } else if (att.coverId && att.coverId < 100000) {
          coverGeneralFileId = att.coverId;
        } else if (attachmentsDirtyRef.current) {
          coverGeneralFileId = null;
        }

        const currentCoverAtt = att.existing.find(
          (a) => (coverGeneralFileId && a.id === coverGeneralFileId) || a.isCover,
        );
        const coverImageScale = currentCoverAtt ? currentCoverAtt.coverScale ?? null : null;
        const coverImagePosX = currentCoverAtt ? currentCoverAtt.coverPosX ?? null : null;
        const coverImagePosY = currentCoverAtt ? currentCoverAtt.coverPosY ?? null : null;

        submitProduct({
          ...formData,
          coverImageScale,
          coverImagePosX,
          coverImagePosY,
          attachmentMeta: {
            pendingFiles: [...att.selectedFiles],
            baseUrls,
            shouldPersistAttachments,
            shouldPersistCover: attachmentsDirtyRef.current,
            ...(coverGeneralFileId !== undefined ? { coverGeneralFileId } : {}),
            coverImageScale,
            coverImagePosX,
            coverImagePosY,
          },
        });
      } catch (error: any) {
        const isConflictError = error?.response?.status === 409 ||
          error?.message?.includes('script duplicado') ||
          error?.message?.includes('já existe') ||
          error?.message?.includes('duplicado');

        if (!isConflictError) {
          setFeedback({
            message: 'Erro interno ao processar dados do formulário.',
            isOpen: true,
            statusOk: false,
            timeout: 5000
          });
        }
      }
  }, [methods, submitProduct, setFeedback]);

  useEffect(() => {
    setSaveOrderHandler(handleSave);
    return () => setSaveOrderHandler(null);
  }, [handleSave, setSaveOrderHandler]);

  const handleTabNavigation = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab' && errorFieldsForNavigation.length > 0) {
      event.preventDefault();

      const fieldElements = errorFieldsForNavigation
        .map(name => document.querySelector<HTMLElement>(`[name="${name}"]`))
        .filter((el): el is HTMLElement => el !== null);

      fieldElements.sort((a, b) => {
        const pos = a.compareDocumentPosition(b);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });

      const sortedFieldNames = fieldElements.map(el => el.getAttribute('name')!);
      const focusedElement = document.activeElement as HTMLElement;

      let currentIndex = -1;
      if (focusedElement) {
        const focusedFieldName = focusedElement.getAttribute('name');
        if (focusedFieldName) {
          currentIndex = sortedFieldNames.indexOf(focusedFieldName);
        }
      }

      if (currentIndex === -1) {
        if (sortedFieldNames.length > 0) {
          methods.setFocus(sortedFieldNames[0] as any);
        }
        return;
      }

      const nextIndex = event.shiftKey
        ? (currentIndex - 1 + sortedFieldNames.length) % sortedFieldNames.length
        : (currentIndex + 1) % sortedFieldNames.length;

      const nextFieldName = sortedFieldNames[nextIndex];
      if (nextFieldName) {
        methods.setFocus(nextFieldName as any);
      }
    }
  }, [errorFieldsForNavigation, methods]);

  const resolveProductAttachmentAccessUrl = useCallback(
    async (attachment: ExistingAttachment, action: 'view' | 'download') => {
      const fallbackSource = attachment.filePath || attachment.fileUrl || '';
      if (fallbackSource) {
        try {
          const signedUrl = await getSignedAttachmentUrl(
            fallbackSource,
            action === 'download',
            attachment.originalName || attachment.fileName,
          );
          if (signedUrl) return signedUrl;
        } catch (error) {
          console.warn(error);
        }
      }

      if (isEditMode && productId && attachment.id > 0 && attachment.id < 100000) {
        const url = await getProductAttachmentActionUrl(productId, attachment.id, action);
        if (url) return url;
      }
      return fallbackSource || null;
    },
    [isEditMode, productId],
  );

  const hydrateSignedPreviewUrls = useCallback(
    async (attachments: ExistingAttachment[]) => {
      if (!attachments.length) return;
      const previewsToHydrate = attachments.filter((attachment) => {
        const source = attachment.filePath || attachment.fileUrl || '';
        const mimeType = String(attachment.mimeType || '').toLowerCase();
        const fileReference = String(
          attachment.originalName || attachment.fileName || source,
        ).toLowerCase();
        const isImageByType = attachment.isImage === true || mimeType.startsWith('image/');
        const isImageByExtension = /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i.test(fileReference);

        return Boolean(source) && (isImageByType || isImageByExtension);
      });
      if (!previewsToHydrate.length) return;

      const hydratedEntries = await Promise.all(
        previewsToHydrate.map(async (attachment) => {
          const signedUrl = await resolveProductAttachmentAccessUrl(attachment, 'view');
          return { id: attachment.id, signedUrl };
        }),
      );

      const hydratedById = new Map<number, string>();
      hydratedEntries.forEach((entry) => {
        if (entry.signedUrl) {
          hydratedById.set(entry.id, entry.signedUrl);
        }
      });

      if (!hydratedById.size) return;

      setProductExistingAttachments((prev) =>
        prev.map((attachment) => {
          const signedUrl = hydratedById.get(attachment.id);
          if (!signedUrl) return attachment;
          return {
            ...attachment,
            filePath: signedUrl,
          };
        }),
      );
    },
    [resolveProductAttachmentAccessUrl],
  );

  const handleProductAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    attachmentsDirtyRef.current = true;
    setProductSelectedFiles((prev) => [...prev, ...files]);
    if (event.target) event.target.value = '';
  };

  const handleProductAttachmentRemove = (index: number) => {
    setProductSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProductSelectFromGallery = (galleryFiles: ExistingAttachment[]) => {
    attachmentsDirtyRef.current = true;
    const newUrls: string[] = [];
    const newExisting: ExistingAttachment[] = [];

    galleryFiles.forEach((file) => {
      const fileUrl = file.fileUrl || file.filePath || '';
      if (fileUrl) {
        newUrls.push(fileUrl);
        newExisting.push({
          ...file,
          filePath: file.filePath || fileUrl,
          fileUrl,
        });
      }
    });

    setProductAttachmentUrls((prev) => Array.from(new Set([...prev, ...newUrls])));
    setProductExistingAttachments((prev) => {
      const existingIds = new Set(prev.map((a) => a.id));
      const toAdd = newExisting.filter((a) => !existingIds.has(a.id));
      return [...prev, ...toAdd];
    });
    if (newExisting.length > 0) {
      void hydrateSignedPreviewUrls(newExisting);
    }
  };

  const handleViewProductExistingAttachment = async (attachment: ExistingAttachment) => {
    const url = await resolveProductAttachmentAccessUrl(attachment, 'view');
    if (!url) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Não foi possível abrir o anexo.' });
      return;
    }
    await openSignedAttachment(url);
  };

  const handleDownloadProductExistingAttachment = async (attachment: ExistingAttachment) => {
    const url = await resolveProductAttachmentAccessUrl(attachment, 'download');
    if (!url) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Não foi possível baixar o anexo.' });
      return;
    }
    await downloadRemoteFile(url, attachment.originalName || attachment.fileName || 'anexo');
  };

  const handleProductExistingAttachmentRemove = (attachmentId: number) => {
    const target = productExistingAttachments.find((a) => a.id === attachmentId);
    if (!target) return;

    attachmentsDirtyRef.current = true;
    setProductExistingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    setProductAttachmentUrls((prev) =>
      prev.filter((url) => {
        const persistable = target.fileUrl || target.filePath?.split('?')[0] || '';
        return (
          url !== persistable &&
          url !== target.fileUrl &&
          url !== target.filePath &&
          url.split('?')[0] !== persistable.split('?')[0]
        );
      }),
    );
    if (coverAttachmentId === attachmentId) {
      setCoverAttachmentId(null);
    }
  };

  const handleSetProductAttachmentCover = async (
    attachment: ExistingAttachment,
    scale?: number,
    posX?: number,
    posY?: number,
  ) => {
    attachmentsDirtyRef.current = true;
    const attachmentSource = attachment.fileUrl || attachment.filePath || '';
    const updatedExisting = attachmentsRef.current.existing.map((item) => {
      const itemSource = item.fileUrl || item.filePath || '';
      const isSameItemById = item.id === attachment.id;
      const isSameItemBySource = Boolean(attachmentSource) && itemSource === attachmentSource;
      const isSameItem = isSameItemById || isSameItemBySource;

      return {
        ...item,
        isCover: isSameItem,
        coverScale: isSameItem ? (typeof scale === 'number' ? scale : item.coverScale) : item.coverScale,
        coverPosX: isSameItem ? (typeof posX === 'number' ? posX : item.coverPosX) : item.coverPosX,
        coverPosY: isSameItem ? (typeof posY === 'number' ? posY : item.coverPosY) : item.coverPosY,
      };
    });
    attachmentsRef.current.existing = updatedExisting;
    setProductExistingAttachments(updatedExisting);
    setCoverAttachmentId(attachment.id < 100000 ? attachment.id : null);

    if (isEditMode && productId && attachment.id > 0 && attachment.id < 100000) {
      try {
        await setProductAttachmentCover(productId, attachment.id, scale, posX, posY);
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } catch (err) {
        console.error('Erro ao definir capa e escala do produto:', err);
      }
    }
  };

  const handleRemoveProductAttachmentCover = () => {
    attachmentsDirtyRef.current = true;
    setProductExistingAttachments((prev) => prev.map((item) => ({ ...item, isCover: false })));
    setCoverAttachmentId(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over || !active.data.current) return;
    if (active.id === over.id) return;

    const scriptIndex = parseInt(active.data.current.scriptIndex || '0');

    if (scriptIndex >= 0 && scriptIndex < scripts.length && Array.isArray(watchedScripts) && watchedScripts[scriptIndex]) {
      const script = watchedScripts[scriptIndex];

      if (!script.items || script.items.length === 0) return;

      const oldIndex = script.items.findIndex((item: any) => getSortableId(item) === String(active.id));
      const newIndex = script.items.findIndex((item: any) => getSortableId(item) === String(over.id));

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newItems = arrayMove(script.items, oldIndex, newIndex);
        const updatedItems = newItems.map((item: any, index: number) => ({ ...item, order: index + 1 }));

        updateScriptField(scriptIndex, {
          ...script,
          items: updatedItems
        });
      }
    }
  };

  const toggleScriptExpansion = (scriptIndex: number) => {
    setExpandedScripts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scriptIndex)) {
        newSet.delete(scriptIndex);
      } else {
        newSet.add(scriptIndex);
      }
      return newSet;
    });
  };

  const addNewScript = () => {
    const newScript = {
      id: Date.now(),
      name: '',
      productId: 1,
      resources: [],
      stages: [],
      materials: [],
      products: [],
      items: []
    };
    appendScript(newScript);
    const newScriptIndex = scripts.length;
    setExpandedScripts(prev => new Set([...prev, newScriptIndex]));
  };

  const handleDuplicateScript = (scriptIndex: number) => {
    const scriptToDuplicate = Array.isArray(watchedScripts) ? watchedScripts[scriptIndex] : null;
    if (!scriptToDuplicate) return;
    
    const duplicatedItems = scriptToDuplicate.items?.map((item: any, index: number) => ({
      ...item,
      id: Date.now() + index,
      realId: undefined, 
    })) || [];

    const namePrefix = scriptToDuplicate.name ? scriptToDuplicate.name : `Script ${scriptIndex + 1}`;
    
    const newScript = {
      ...scriptToDuplicate,
      id: Date.now() + 1000, 
      name: `Cópia ${namePrefix}`,
      items: duplicatedItems
    };

    appendScript(newScript);
    const newScriptIndex = scripts.length;
    setExpandedScripts(prev => new Set([...prev, newScriptIndex]));
    setScriptMenuOpen(null);
  };

  const handleRemoveScript = (scriptIndex: number) => {
    setScriptToDelete(scriptIndex);
    setShowDeleteScriptModal(true);
  };

  const confirmRemoveScript = () => {
    if (scriptToDelete === null) return;
    if (scripts.length > 1) {
      removeScriptField(scriptToDelete);
      setExpandedScripts(prev => {
        const newSet = new Set(prev);
        newSet.delete(scriptToDelete);
        const adjustedSet = new Set<number>();
        newSet.forEach(index => {
          if (index < scriptToDelete) {
            adjustedSet.add(index);
          } else if (index > scriptToDelete) {
            adjustedSet.add(index - 1);
          }
        });
        return adjustedSet;
      });
    }
    setShowDeleteScriptModal(false);
    setScriptToDelete(null);
  };

  const cancelRemoveScript = () => {
    setShowDeleteScriptModal(false);
    setScriptToDelete(null);
  };

  const addNewItem = (scriptIndex: number) => {
    setCurrentScriptIndex(scriptIndex);
    setIsAddItemModalOpen(true);
  };

  const handleSelectItemType = (type: 'rawMaterial' | 'service' | 'resource' | 'productFinal') => {
    const newItemId = Date.now();

    const newItem = {
      id: newItemId,
      type,
      order: (Array.isArray(watchedScripts) && watchedScripts[currentScriptIndex!]?.items?.length || 0) + 1,
      rawMaterial: null,
      service: null,
      resource: null,
      productFinal: null,
      weight: undefined,
      idealMeasurements: '',
      productScript: null,
      quantity: undefined,
      readyToExecute: false,
      executionTime: null,
      setupTime: null,
      requiredQualification: '',
      deliveryTime: undefined,
    };

    const script = Array.isArray(watchedScripts) ? watchedScripts[currentScriptIndex!] : null;
    if (!script) return;
    const updatedItems = [...(script.items || []), newItem];
    updateScriptField(currentScriptIndex!, { ...script, items: updatedItems });

    if (errors.scripts?.[currentScriptIndex!]?.items) {
      methods.clearErrors(`scripts.${currentScriptIndex!}.items`);
    }

    setExpandedItems(prev => new Set(prev).add(newItemId));

    setCurrentScriptIndex(null);
    setIsAddItemModalOpen(false);
  };

  const handleRemoveItem = (scriptIndex: number, itemId: string | number) => {
    setItemToDelete({ scriptIndex, itemId });
    setShowDeleteItemModal(true);
  };

  const confirmRemoveItem = () => {
    if (!itemToDelete) return;
    const { scriptIndex, itemId } = itemToDelete;

    const script = Array.isArray(watchedScripts) ? watchedScripts[scriptIndex] : null;
    if (!script) return;
    const updatedItems = script.items?.filter((item: any) => item.id !== itemId) || [];

    updateScriptField(scriptIndex, {
      ...script,
      items: updatedItems.map((item: any, index: number) => ({ ...item, order: index + 1 }))
    });

    setShowDeleteItemModal(false);
    setItemToDelete(null);
  };

  const cancelRemoveItem = () => {
    setShowDeleteItemModal(false);
    setItemToDelete(null);
  };

  const moveItem = (scriptIndex: number, itemId: string | number, direction: 'up' | 'down') => {
    const script = Array.isArray(watchedScripts) ? watchedScripts[scriptIndex] : null;
    if (!script) return;
    const items = script.items || [];
    const currentIndex = items.findIndex((item: any) => item.id === itemId);

    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= items.length) return;

    const newItems = [...items];
    [newItems[currentIndex], newItems[newIndex]] = [newItems[newIndex], newItems[currentIndex]];

    const updatedItems = newItems.map((item: any, index: number) => ({ ...item, order: index + 1 }));
    updateScriptField(scriptIndex, { ...script, items: updatedItems });
  };

  const toggleItemExpansion = (itemId: string | number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleItemTypeChange = useCallback((scriptIndex: number, itemIndex: number, newType: string) => {
    const script = Array.isArray(watchedScripts) ? watchedScripts[scriptIndex] : null;
    if (!script || !script.items) return;

    const item = script.items[itemIndex];
    if (!item) return;

    const newItem = {
      id: item.id,
      order: item.order,
      type: newType,
      rawMaterial: null,
      service: null,
      resource: null,
      productFinal: null,
      weight: undefined,
      idealMeasurements: '',
      productScript: null,
      quantity: undefined,
      readyToExecute: false,
      executionTime: null,
      setupTime: null,
      requiredQualification: '',
      deliveryTime: undefined,
    };

    const newItems = [...script.items];
    newItems[itemIndex] = newItem;

    updateScriptField(scriptIndex, {
      ...script,
      items: newItems
    });
  }, [watchedScripts, updateScriptField]);

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'rawMaterial': return 'Matéria Prima';
      case 'service': return 'Serviço';
      case 'resource': return 'Insumo';
      case 'productFinal': return 'Produto Manufaturado';
      default: return type;
    }
  };

  const { getServiceNameById, getRawMaterialNameById, getResourceNameById, getProductNameById } = useMemo(() => ({
    getServiceNameById: (id: number | undefined) => {
      return servicesQuery.data?.find(service => service.id === id)?.name || '';
    },
    getRawMaterialNameById: (id: number | undefined) => {
      return rawMaterialsQuery.data?.find(material => material.id === id)?.name || '';
    },
    getResourceNameById: (id: number | undefined) => {
      return resourcesQuery.data?.find(resource => resource.id === id)?.name || '';
    },
    getProductNameById: (id: number | undefined) => {
      return productsQuery.data?.find(product => product.id === id)?.name || '';
    },
  }), [servicesQuery.data, rawMaterialsQuery.data, resourcesQuery.data, productsQuery.data]);

  if (isLoadingProduct) {
    return <OrdenoLoading variant="fullscreen" text="Carregando..." />;
  }

  if (!watchedScripts) {
    return <OrdenoLoading variant="fullscreen" text="Inicializando formulário..." />;
  }

  if ((isEditMode || isDuplicateMode) && !isFormReady) {
    return (
      <OrdenoLoading
        variant="fullscreen"
        text={isEditMode ? 'Carregando dados do produto...' : 'Carregando dados para duplicação...'}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Container onKeyDown={handleTabNavigation}>
          <PageGrid $asideOpen={isAttachmentsAsideOpen}>
            <FormColumn>
              <Card>
            <SubTitle>Geral</SubTitle>
            <FormGrid columns={3}>
              <FormGroup>
                <Label>
                  Nome do produto
                  <Tooltip text={errors.name?.message || ''}>
                    <AlertCircle size={16} color={errors.name ? "#F87171" : "#696969"} />
                  </Tooltip>
                </Label>
                <Input
                  placeholder="Digite aqui"
                  {...methods.register('name')}
                  hasError={!!errors.name}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Label>Código</Label>
                <Input
                  placeholder="Código do produto"
                  {...methods.register('productCode')}
                  hasError={!!errors.productCode}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Label>Norma</Label>
                <Input
                  placeholder="Digite a norma"
                  {...methods.register('norm')}
                  hasError={!!errors.norm}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  placeholder="Digite a quantidade"
                  {...methods.register('quantity', {
                    valueAsNumber: true,
                    setValueAs: (value: string) => value === '' ? undefined : parseInt(value)
                  })}
                  hasError={!!errors.quantity}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Label>NCM</Label>
                <InfiniteSelect
                  mode="crud"
                  value={ncm}
                  onSelect={(option) => {
                    const escolhido = ncmDaOpcao(option);
                    setNcm(escolhido);
                    setValue('ncmId', escolhido ? escolhido.id : null, { shouldDirty: true });
                  }}
                  endpointConfig={ncmCatalogoEndpoint}
                  placeholder="Buscar NCM (opcional)"
                  emptyOptionsMessage="Nenhum item disponível"
                  errorOptionsMessage="Código não existe na tabela oficial de NCM"
                  allowCreate={false}
                  allowDelete={false}
                />
              </FormGroup>
              <FormGroup>
                <Label>Formato</Label>
                <Select
                  {...methods.register('formatId', {
                    setValueAs: (value: string) => value === '' ? null : Number(value),
                  })}
                  hasError={!!errors.formatId}
                >
                  <option value="">Selecione (opcional)</option>
                  {formats.map((format) => (
                    <option key={format.id} value={format.id}>{format.name}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Unidade</Label>
                <Select
                  {...methods.register('unitOfMeasurementId', {
                    setValueAs: (value: string) => value === '' ? null : Number(value),
                  })}
                  hasError={!!errors.unitOfMeasurementId}
                >
                  <option value="">Selecione (opcional)</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>{unit.name}</option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </Card>

          {Array.isArray(watchedScripts) && watchedScripts.map((script: any, scriptIndex) => {
            const isExpanded = expandedScripts.has(scriptIndex);

            return (
              <Card key={script.id}>
                <CollapsibleHeader style={{ alignItems: 'flex-start' }}>
                  <FormGrid columns={2} style={{ flex: 1, gap: '1rem' }}>
                    <FormGroup>
                      <Label>
                        Nome do script
                        <Tooltip text={errors.scripts?.[scriptIndex]?.name?.message || ''}>
                          <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.name ? "#F87171" : "#696969"} />
                        </Tooltip>
                      </Label>
                      <Input
                        {...methods.register(`scripts.${scriptIndex}.name`)}
                        placeholder="Digite aqui"
                        hasError={!!errors.scripts?.[scriptIndex]?.name}
                        autoComplete="off"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Prazo adicional (dias)</Label>
                      <Input
                        type="number"
                        {...methods.register(`scripts.${scriptIndex}.additionalDeadline`, {
                          valueAsNumber: true,
                          setValueAs: (value: string) => {
                            if (value === '' || value === null || value === undefined) return undefined;
                            const num = parseInt(value);
                            return isNaN(num) ? undefined : num;
                          }
                        })}
                        placeholder="Ex: 5"
                        autoComplete="off"
                      />
                    </FormGroup>
                  </FormGrid>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div 
                      style={{ position: 'relative' }} 
                      tabIndex={-1}
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                          setScriptMenuOpen(null);
                        }
                      }}
                    >
                      <Button
                        type="button"
                        variant="outline"
                        style={{ padding: '0px 4px', border: 'none', background: 'transparent' }}
                        onClick={(e) => {
                           e.preventDefault();
                           setScriptMenuOpen(scriptMenuOpen === scriptIndex ? null : scriptIndex);
                        }}
                      >
                        <MoreHorizontal size={24} color="#696969" />
                      </Button>
                      {scriptMenuOpen === scriptIndex && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          backgroundColor: '#fff',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          zIndex: 10,
                          minWidth: '160px',
                          padding: '4px'
                        }}>
                          <button
                            type="button"
                            onMouseDown={() => handleDuplicateScript(scriptIndex)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              fontSize: '14px',
                              textAlign: 'left',
                              color: '#334155'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Copy size={14} />
                            Duplicar Script
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '28px' }}>
                      {Array.isArray(watchedScripts) && watchedScripts.length > 1 && (
                        <Button
                          variant="danger-outline"
                          onClick={() => handleRemoveScript(scriptIndex)}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                      <CircularCollapseIcon onClick={() => toggleScriptExpansion(scriptIndex)}>
                        <ChevronDown
                          size={16}
                          style={{
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      </CircularCollapseIcon>
                    </div>
                  </div>
                </CollapsibleHeader>

                {isExpanded && (
                  <>
                    <Separator />
                    <SubTitle style={{ marginTop: '1rem' }}>Itens do script</SubTitle>

                    <SortableContext
                      items={script.items?.map((item: any) => getSortableId(item)) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      {script.items?.map((item: any, index: number) => (
                        <SortableItem
                          key={getSortableId(item)}
                          item={item}
                          scriptIndex={scriptIndex}
                          index={index}
                          totalItems={script.items?.length || 0}
                          onRemove={handleRemoveItem}
                          onToggleExpansion={toggleItemExpansion}
                          onMoveItem={moveItem}
                          expandedItems={expandedItems}
                          getItemTypeLabel={getItemTypeLabel}
                          control={control}
                          setValue={setValue}
                          watch={watch}
                          errors={errors}
                          getValues={getValues}
                          fetchRawMaterials={fetchRawMaterials}
                          fetchResources={fetchResources}
                          fetchProducts={fetchProductsFiltered}
                          fetchServices={fetchServices}
                          updateItemWeight={updateItemWeight}
                          updateItemIdealMeasurements={updateItemIdealMeasurements}
                          updateItemProductScript={updateItemProductScript}
                          updateItemReadyToExecute={updateItemReadyToExecute}
                          updateItemUnitOfMeasurement={updateItemUnitOfMeasurement}
                          validateProductFinalSelection={validateProductFinalSelection}
                          handleItemTypeChange={handleItemTypeChange}
                          fetchCompaniesByStage={fetchCompaniesByStage}
                        />
                      ))}
                    </SortableContext>

                    <AddNewItemButton>
                      {(() => {
                        const hasError = Boolean(
                          errors.scripts?.[scriptIndex]?.items?.message === 'O script deve ter pelo menos um item.' ||
                          (errors.scripts?.[scriptIndex]?.items && !Array.isArray(errors.scripts[scriptIndex].items) && errors.scripts[scriptIndex].items.message === 'O script deve ter pelo menos um item.') ||
                          (errors.scripts?.[scriptIndex]?.items && typeof errors.scripts[scriptIndex].items === 'object' && errors.scripts[scriptIndex].items.message === 'O script deve ter pelo menos um item.') ||
                          Boolean(errors.scripts?.[scriptIndex]?.items?.root)
                        ) as boolean;

                        return (
                          <AddProductButton
                            variant="outline"
                            hasError={hasError}
                            onClick={() => addNewItem(scriptIndex)}
                          >
                            <Plus size={16} style={{ marginRight: '8px' }} />
                            Adicionar item
                          </AddProductButton>
                        );
                      })()}
                    </AddNewItemButton>
                  </>
                )}
              </Card>
            );
          })}

          <AddNewItemButton>
            <AddProductButton variant="primary" onClick={addNewScript}>
              <Plus size={16} style={{ marginRight: '8px' }} />
              Adicionar Script
            </AddProductButton>
          </AddNewItemButton>
        </FormColumn>

        <AsideColumn>
          <ProductAttachmentsAside
            selectedFiles={productSelectedFiles}
            existingAttachments={productExistingAttachments}
            onFileChange={handleProductAttachmentChange}
            onRemoveFile={handleProductAttachmentRemove}
            onViewAttachment={handleViewProductExistingAttachment}
            onDownloadAttachment={handleDownloadProductExistingAttachment}
            onRemoveExistingAttachment={handleProductExistingAttachmentRemove}
            onSetCoverAttachment={handleSetProductAttachmentCover}
            onRemoveCoverAttachment={handleRemoveProductAttachmentCover}
            onSelectFromGallery={handleProductSelectFromGallery}
            formatFileSize={(bytes) => `${(bytes / 1024).toFixed(1)} KB`}
            isOpen={isAttachmentsAsideOpen}
            onToggleOpen={(next) => setIsAttachmentsAsideOpen(next)}
          />
        </AsideColumn>
      </PageGrid>

          <AddItemModal
            isOpen={isAddItemModalOpen}
            onClose={() => {
              setIsAddItemModalOpen(false);
              setCurrentScriptIndex(null);
            }}
            onSelectType={handleSelectItemType}
          />

          {showDeleteScriptModal && (
            <ConfirmationModal
              isOpen={showDeleteScriptModal}
              title="Confirmar Exclusão de Script"
              message="Você tem certeza que deseja excluir este script? Todos os seus itens serão perdidos."
              actions={[
                { label: 'Confirmar', onClick: confirmRemoveScript, variant: 'danger' },
                { label: 'Voltar', onClick: cancelRemoveScript, variant: 'outline' }
              ]}
            />
          )}

          {showDeleteItemModal && (
            <ConfirmationModal
              isOpen={showDeleteItemModal}
              title="Confirmar Exclusão de Item"
              message="Você tem certeza que deseja excluir este item?"
              actions={[
                { label: 'Confirmar', onClick: confirmRemoveItem, variant: 'danger' },
                { label: 'Voltar', onClick: cancelRemoveItem, variant: 'outline' }
              ]}
            />
          )}

          {validationErrorModal.isOpen && (
            <ConfirmationModal
              isOpen={validationErrorModal.isOpen}
              title="Campos Obrigatórios"
              message={validationErrorModal.message}
              actions={[
                {
                  label: 'Entendi',
                  onClick: () => {
                    const fields = validationErrorModal.errorFields;
                    setValidationErrorModal({ isOpen: false, message: '', errorFields: [] });
                    if (fields.length > 0) {
                      methods.setFocus(fields[0] as any);
                    }
                  },
                  variant: 'primary'
                }
              ]}
            />
          )}

          {showCancelModal && (
            <ConfirmationModal
              isOpen={showCancelModal}
              title={isEditMode ? "Cancelar Edição de Produto" : "Cancelar Criação de Produto"}
              message="Você tem certeza? Todas as alterações não salvas serão perdidas."
              actions={[
                { label: 'Confirmar', onClick: () => navigate(returnTo || '/panel/product-final'), variant: 'danger' },
                { label: 'Voltar', onClick: () => setShowCancelModal(false), variant: 'outline' }
              ]}
            />
          )}
        </Container>
      </DndContext>
    </FormProvider>
  );
};

export default AddProduct;